import { useMemo } from "react";
import { useDebounce } from "use-debounce";

import {
  ModelSearchResultFragment,
  useSearchModelsQuery,
} from "@/apollo/types";
import { formatModelName } from "@/features/models";

type UseModelSearchOptions = {
  debounceMs?: number;
  publishedOnly?: boolean;
};

type ModelSearchResultItem = ModelSearchResultFragment & {
  isPublished: boolean;
};

export function useModelSearch(
  query: string,
  options: UseModelSearchOptions = {},
) {
  const { debounceMs = 300, publishedOnly = true } = options;
  const [debouncedQuery] = useDebounce(query, debounceMs);

  const searchResult = useSearchModelsQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      query: debouncedQuery,
      publishedOnly,
    },
    skip: debouncedQuery.trim().length === 0,
  });

  const models = useMemo(() => {
    const models = searchResult.data?.searchModels ?? [];
    return [...models].sort(modelsSorter).map(
      (model): ModelSearchResultItem => ({
        ...model,
        isPublished: model.publishedQueryId != null,
      }),
    );
  }, [searchResult.data?.searchModels]);

  return {
    ...searchResult,
    isSearching: searchResult.loading,
    models: models,
  };
}

function modelsSorter(
  a: ModelSearchResultFragment,
  b: ModelSearchResultFragment,
): number {
  const aName = formatModelName(a).toLowerCase();
  const bName = formatModelName(b).toLowerCase();
  return aName.localeCompare(bName);
}
